<template>
  <section aria-label="AI preliminary conversation">
    <h3 v-if="!dialogue.gate">Talk with your AI assistant</h3>
    <div v-for="message in dialogue.messages" :key="message.id" class="dialogue-message">
      <strong>{{ message.role === 'assistant' ? 'AI' : 'You' }}</strong>
      <p>{{ message.content || (message.status === 'pending' ? 'Preparing reply…' : 'This reply could not be completed.') }}</p>
    </div>
    <p v-if="state.pending" role="status">The AI is preparing its reply. Your conversation is saved.</p>
    <p v-if="state.blocked" role="alert">This conversation needs administrator attention. Your writing is saved; feedback has not been unlocked by skipping the question.</p>
    <form v-if="!answered && dialogue.gate && group !== 'A'" @submit.prevent="sendGate">
      <fieldset v-if="dialogue.gate.kind === 'criteria_selection'" :disabled="!state.canReply || busy">
        <legend>Select dimensions to improve</legend>
        <label class="criterion-choice"><input type="checkbox" :checked="allSelected" @change="selectAll($event.target.checked)" />Select all dimensions</label>
        <label v-for="criterion in dialogue.gate.criteria" :key="criterion.id" class="criterion-choice">
          <input type="checkbox" v-model="selected" :value="criterion.id" />
          <span>{{ criterion.label }}<small>{{ criterion.description }}</small></span>
        </label>
      </fieldset>
      <fieldset v-else :disabled="!state.canReply || busy">
        <legend>Rate every aspect: 1 = Poor; 7 = Excellent</legend>
        <div class="rating-scroll"><table class="criterion-ratings">
          <thead><tr><th scope="col">Rubric aspect</th><th v-for="score in 7" :key="score" scope="col">{{ score }}</th></tr></thead>
          <tbody><tr v-for="criterion in dialogue.gate.criteria" :key="criterion.id">
            <th scope="row">{{ criterion.label }}<small>{{ criterion.description }}</small></th>
            <td v-for="score in 7" :key="score"><input type="radio" :name="`criterion-${criterion.id}`" :value="score" v-model="ratings[criterion.id]" :aria-label="`${criterion.label}: ${score}`" /></td>
          </tr></tbody>
        </table></div>
      </fieldset>
      <button class="ui-button ui-button--primary" :disabled="!state.canReply || busy || !gateComplete" type="submit">Confirm and show feedback</button>
    </form>
    <form v-else-if="!answered && group !== 'A'" @submit.prevent="send">
      <label for="group-dialogue-reply">Your reply</label>
      <textarea id="group-dialogue-reply" class="ui-input" v-model="text" maxlength="2000" :disabled="!state.canReply || busy" :placeholder="group === 'C' ? 'Give one overall score from 1 to 4, with an optional explanation.' : 'Tell the AI which rubric aspect you want to improve first.'" />
      <small v-if="group === 'C'">Use one number (1, 2, 3 or 4) for the whole section.</small>
      <button class="ui-button ui-button--primary" :disabled="!state.canReply || busy || !text.trim()" type="submit">Send reply</button>
    </form>
  </section>
</template>
<script setup>
import { ref, computed, watch } from 'vue'
import { dialogueState } from './dialogue-state.mjs'
const props = defineProps({ dialogue: Object, group: String, answered: Boolean, dirty: Boolean, writable: Boolean, busy: Boolean })
const emit = defineEmits(['reply'])
const text = ref('')
const selected = ref([]), ratings = ref({})
const state = computed(() => dialogueState(props.dialogue, props.dirty, props.writable))
const allSelected = computed(() => props.dialogue.gate?.criteria.every(criterion => selected.value.includes(criterion.id)))
const gateComplete = computed(() => props.dialogue.gate?.kind === 'criteria_selection' ? selected.value.length > 0 : props.dialogue.gate?.criteria.every(criterion => Number.isInteger(ratings.value[criterion.id]) && ratings.value[criterion.id] >= 1 && ratings.value[criterion.id] <= 7))
function selectAll(checked) { selected.value = checked ? props.dialogue.gate.criteria.map(criterion => criterion.id) : [] }
function sendGate() {
  if (!state.value.canReply || props.busy || !gateComplete.value) return
  emit('reply', props.dialogue.gate.kind === 'criteria_selection'
    ? props.dialogue.gate.criteria.filter(criterion => selected.value.includes(criterion.id)).map(criterion => criterion.id)
    : Object.fromEntries(props.dialogue.gate.criteria.map(criterion => [criterion.id, ratings.value[criterion.id]])))
}
function send() { if (state.value.canReply && !props.busy && text.value.trim()) emit('reply', text.value.trim()) }
watch(() => props.dialogue.messages.filter(m => m.role === 'user').length, () => { text.value = '' })
</script>
<style scoped>
.dialogue-message { border-bottom: 1px solid var(--border); padding: 12px 0; }
p { white-space: pre-wrap; overflow-wrap: anywhere; line-height: 1.6; }
textarea { display: block; width: 100%; box-sizing: border-box; min-height: 92px; margin: 8px 0; }
small { display: block; color: var(--ink-muted); margin-bottom: 12px; }
fieldset { border: 0; padding: 0; margin: 16px 0; min-width: 0; }
legend { font-weight: 600; margin-bottom: 10px; }
.criterion-choice { display: flex; gap: 10px; align-items: flex-start; padding: 10px 0; }
.criterion-choice input { flex-shrink: 0; margin-top: 4px; }
.criterion-choice small { margin: 5px 0 0; }
.rating-scroll { overflow-x: auto; }
.criterion-ratings { border-collapse: collapse; width: 100%; font-size: 13px; }
.criterion-ratings th, .criterion-ratings td { padding: 10px 4px; border-bottom: 1px solid var(--border); text-align: center; }
.criterion-ratings th:first-child { text-align: left; min-width: 115px; }
.criterion-ratings small { font-weight: normal; margin: 5px 0 0; }
.criterion-ratings input { width: 16px; height: 16px; margin: 0; }
</style>
